import { useEffect, useState } from "react";
import { ServiceInfoDc, TableInfoDc } from "@evergis/api";

import { getLayerInfo } from "../../../../../evergis/core/layer/getLayerInfo";
import { isReadable } from "../../../../../evergis/core/permissions";
import { extractResourceName } from "../../../../../utils/resourceName";
import { project } from "../../../../../resources/projects";
import { useProjectDeps } from "./useProjectDeps";
import { useDefaultScaleRange } from "../../../../../hooks/style/useDefaultScaleRange";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";
import { ProjectStructureInfo } from "../../types";

export const useProjectInfo = (projectName: string) => {
  const [projectStructure, setProjectStructure] = useState<ProjectStructureInfo | undefined>(undefined);
  const [structureLoading, setStructureLoading] = useState<boolean>(true);
  const { data: projectInfo, refetch } = project.use({ input: projectName, waitInput: true, immediate: false });
  const { tables, layers, loading: depsLoading } = useProjectDeps(projectName);
  const defaultScaleRange = useDefaultScaleRange();

  useEffect(() => {
    refetch().then(() => setStructureLoading(false));
  }, [refetch]);

  useEffect(() => {
    if (!projectInfo || !tables || !layers) {
      return undefined;
    }

    const filteredTables: TableInfoDc[] = (tables && tables.filter(table => isReadable(table.permissions))) || [];

    setProjectStructure({
      ...projectInfo,
      tables: filteredTables.map((table: TableInfoDc) => {
        return {
          ...table,
          layers: (Array.isArray(layers)
            ? layers.reduce<LayerInfo[]>((acc, layer): LayerInfo[] => {
                const layerInfo = getLayerInfo(layer, defaultScaleRange);

                if (
                  !isReadable(layer.permissions) ||
                  extractResourceName(layerInfo.tableName) !== extractResourceName(table.name)
                ) {
                  return acc;
                }

                return [...acc, layerInfo];
              }, [])
            : []) as ServiceInfoDc[],
        };
      }),
    } as ProjectStructureInfo);
  }, [projectInfo, tables, layers]);

  return { projectStructure, loading: structureLoading || depsLoading };
};
